import type { ActivityLogicPlugin } from '../../core/types/plugin.base.types';
import type {
  CountingCollectionPayload,
  CountingCollectionRuntime,
} from '../../core/types/payloads/counting_collection.types';
import type { HostActionPayload, StudentInputEnvelope } from '../../core/types/events.types';
import type { RoutingMode } from '../../core/types/room.types';
import { CountingCollectionPayloadSchema } from '../../core/schemas/examGenerationSchema';

const MAX_COUNT_PER_GROUP = 20;
const MAX_GROUPS = 6;

function inferStepFromInstruction(instruction: string): number | null {
  const match = String(instruction || '').match(/de\s*(\d+)\s*en\s*(\d+)/i);
  if (!match) return null;
  const step = Number(match[2] || match[1]);
  return Number.isFinite(step) && step > 0 ? step : null;
}

export function validateCountingCollectionPayload(raw: unknown): CountingCollectionPayload {
  const parsed = CountingCollectionPayloadSchema.safeParse(raw);
  if (!parsed.success) {
    const first = parsed.error.issues[0];
    throw new Error(first?.message || 'CountingCollection payload invalid');
  }

  const data = parsed.data;
  if (data.groups.length > MAX_GROUPS) {
    throw new Error(`groups: máximo ${MAX_GROUPS} grupos`);
  }

  const step = data.step ?? inferStepFromInstruction(data.instruction) ?? undefined;
  const seenIds = new Set<string>();
  const groups = data.groups.map((group, index) => {
    if (seenIds.has(group.id)) {
      throw new Error(`groups: id duplicado "${group.id}"`);
    }
    seenIds.add(group.id);
    if (group.count > MAX_COUNT_PER_GROUP) {
      throw new Error(`groups.${group.id}: count no puede superar ${MAX_COUNT_PER_GROUP}`);
    }
    if (group.count <= 0) {
      throw new Error(`groups.${group.id}: count debe ser mayor que 0`);
    }
    if (step && group.count % step !== 0) {
      throw new Error(
        `groups.${group.id}: count=${group.count} debe ser múltiplo del salto ${step} (metáfora de conteo)`,
      );
    }
    if (step && group.count !== step * (index + 1) && data.groups.length > 1) {
      throw new Error(
        `groups.${group.id}: para saltos de ${step}, el grupo ${index + 1} debe tener count=${step * (index + 1)}`,
      );
    }
    return {
      id: group.id,
      itemType: group.itemType,
      count: group.count,
      ...(group.label?.trim() ? { label: group.label.trim() } : {}),
    };
  });

  return {
    instruction: data.instruction.trim(),
    ...(step ? { step } : {}),
    groups,
  };
}

export function createInitialCountingCollectionRuntime(
  _payload: CountingCollectionPayload,
  _routing: RoutingMode,
): CountingCollectionRuntime {
  return { viewed: false, answers: {} };
}

export const countingCollectionLogic: ActivityLogicPlugin<'counting_collection'> = {
  mode: 'counting_collection',
  validatePayload: validateCountingCollectionPayload,
  createInitialRuntime: createInitialCountingCollectionRuntime,

  onStudentInput(ctx, input: StudentInputEnvelope) {
    if (input.action === 'EXAM_CONTINUE' || input.action === 'VIEW') {
      return { runtime: { ...ctx.runtime, viewed: true } };
    }
    if (input.action === 'SUBMIT') {
      const data =
        input.data && typeof input.data === 'object'
          ? (input.data as Record<string, unknown>)
          : {};
      const answers = { ...(ctx.runtime.answers || {}) };
      for (const [key, value] of Object.entries(data)) {
        answers[String(key)] = String(value ?? '').trim();
      }
      return { runtime: { ...ctx.runtime, viewed: true, answers } };
    }
    return { runtime: ctx.runtime, rejected: true, rejectReason: 'UNKNOWN_ACTION' };
  },

  onHostAction(ctx, _action: HostActionPayload) {
    return { runtime: ctx.runtime };
  },
};

export function demoCountingCollectionPayload(): CountingCollectionPayload {
  return validateCountingCollectionPayload({
    instruction: 'La pelota salta de 2 en 2. Cuenta cuántas hay en cada salto.',
    step: 2,
    groups: [
      { id: 'g1', itemType: 'ball', count: 2, label: 'Salto 1' },
      { id: 'g2', itemType: 'ball', count: 4, label: 'Salto 2' },
      { id: 'g3', itemType: 'ball', count: 6, label: 'Salto 3' },
    ],
  });
}
